// Seite `/search`: Volltextsuche (docs/API.md 3.8) mit Bereich, Sortierung, Gruppierung nach Kanal,
// „Mehr laden" (offset/nextOffset) und Sprung zur Nachricht. Zustand steckt in der URL.
import type { ServerDto } from '@lagerfeuer/shared';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo, useState, type KeyboardEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { lookupApi, messageLink, searchApi, type SearchSort } from '../api';
import { displayName, formatNumber } from '../format';
import { MessagePreview } from '../message-preview';
import { Badge, Button, Empty, ExtrasPage, Loading } from '../ui';
import { CHANNEL_TYPE_LABELS, groupByChannel, recentAuthors, searchErrorMessage, shouldRetrySearch, type SearchHit } from './helpers';
import { parseQuery, toSearchParams } from './query-parser';
import { SearchBox } from './SearchBox';
import { readSearchUrl, scopeIds, searchQuery, type ScopeMode, type SearchUrlState } from './url';
import './search.css';

const SORT_LABELS: Record<SearchSort, string> = { newest: 'Neueste zuerst', oldest: 'Älteste zuerst', relevance: 'Relevanz' };

export function SearchPage() {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const state = readSearchUrl(sp);
  const { q, server, channel, sort, scope } = state;

  // Entwurf im Feld; folgt der URL (Zurück-Navigation), ohne Effekt.
  const [draft, setDraft] = useState(q);
  const [prevQ, setPrevQ] = useState(q);
  if (prevQ !== q) {
    setPrevQ(q);
    setDraft(q);
  }

  const servers = useQuery({ queryKey: ['extras', 'search', 'servers'], queryFn: lookupApi.servers, staleTime: 60_000 });
  const serverById = useMemo(() => new Map<string, ServerDto>((servers.data ?? []).map((s) => [s.id, s])), [servers.data]);
  const channelInfo = useQuery({
    queryKey: ['extras', 'search', 'channel', channel],
    queryFn: () => lookupApi.channel(channel as string),
    enabled: !!channel,
    staleTime: 60_000,
  });

  const params = useMemo(() => {
    if (!q.trim()) return null;
    return toSearchParams(parseQuery(q), { ...scopeIds({ q, server, channel, sort, scope }), sort });
  }, [q, server, channel, sort, scope]);

  const results = useInfiniteQuery({
    queryKey: ['extras', 'search', 'messages', params],
    queryFn: ({ pageParam }) => searchApi.messages({ ...params, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last) => last.nextOffset ?? undefined,
    enabled: !!params,
    retry: shouldRetrySearch,
  });

  const hits = useMemo<SearchHit[]>(() => results.data?.pages.flatMap((p) => p.results) ?? [], [results.data]);
  const groups = useMemo(() => groupByChannel(hits), [hits]);
  const authors = useMemo(() => recentAuthors(hits), [hits]);
  const total = results.data?.pages[0]?.totalResults ?? 0;

  const update = (patch: Partial<SearchUrlState>) => setSp(searchQuery({ ...state, ...patch }));

  const submit = (value: string) => {
    const next = value.trim();
    if (!next) return;
    if (next === q) void results.refetch();
    else update({ q: next });
  };

  const isMirroredServer = (id: string | null | undefined) => !!id && !!serverById.get(id)?.isMirrored;
  const jump = (h: SearchHit) => navigate(messageLink(h.message.serverId ?? h.channel.serverId, h.channel.id, h.message.id));

  const serverName = server ? (serverById.get(server)?.name ?? 'Aktueller Server') : null;
  const channelName = channel ? (channelInfo.data?.name ? `#${channelInfo.data.name}` : 'Aktueller Kanal') : null;
  const scopes: { value: ScopeMode; label: string }[] = [
    { value: 'all', label: 'Überall' },
    ...(server ? [{ value: 'server' as const, label: serverName ?? '' }] : []),
    ...(channel ? [{ value: 'channel' as const, label: channelName ?? '' }] : []),
  ];

  return (
    <ExtrasPage title="Suche" testId="search-page">
      <div className="lfx-stack">
        <SearchBox value={draft} onChange={setDraft} onSubmit={submit} serverId={server} recentAuthors={authors} autoFocus busy={results.isFetching && !results.isFetchingNextPage} />

        <div className="lfx-search-controls">
          {scopes.length > 1 && (
            <fieldset className="lfx-search-scope" data-testid="search-scope">
              <legend className="lfx-search-scope__legend">Bereich</legend>
              {scopes.map((s) => (
                <label key={s.value} className={`lfx-search-scope__opt${scope === s.value ? ' lfx-search-scope__opt--active' : ''}`}>
                  <input type="radio" name="search-scope" value={s.value} checked={scope === s.value} onChange={() => update({ scope: s.value })} data-testid={`search-scope-${s.value}`} />
                  {s.label}
                </label>
              ))}
            </fieldset>
          )}
          <label className="lfx-search-sort">
            <span>Sortierung</span>
            <select className="lfx-select" value={sort} data-testid="search-sort" onChange={(e) => update({ sort: e.target.value as SearchSort })}>
              {(Object.keys(SORT_LABELS) as SearchSort[]).map((s) => (
                <option key={s} value={s}>
                  {SORT_LABELS[s]}
                </option>
              ))}
            </select>
          </label>
        </div>

        {!params && (
          <Empty testId="search-intro">
            <p>Gib einen Suchbegriff ein und drücke Enter.</p>
            <p className="lfx-muted">
              Filter: <code>from:name</code> <code>in:kanal</code> <code>has:image</code> <code>mentions:name</code> <code>before:2026-10-05</code>{' '}
              <code>after:…</code> <code>during:…</code> <code>pinned:true</code> · Phrasen in <code>"…"</code>, Ausschluss mit <code>-wort</code>
            </p>
          </Empty>
        )}

        {params && results.isPending && <Loading testId="search-loading" />}

        {params && results.isError && !results.data && (
          <div className="lfx-error" role="alert" data-testid="search-error">
            {searchErrorMessage(results.error)}{' '}
            <Button testId="search-retry" onClick={() => void results.refetch()}>
              Erneut versuchen
            </Button>
          </div>
        )}

        {params && results.data && (
          <section aria-label="Suchergebnisse" className="lfx-stack">
            <div className="lfx-search-total" data-testid="search-total" aria-live="polite">
              {formatNumber(total)} Treffer
            </div>
            {hits.length === 0 ? (
              <Empty testId="search-empty">Keine Treffer für „{q}".</Empty>
            ) : (
              groups.map((g) => {
                const srv = g.serverId ? serverById.get(g.serverId) : undefined;
                const mirroredGroup = isMirroredServer(g.serverId) || g.hits.some((h) => h.message.bridged);
                return (
                  <section key={g.channel.id} className="lfx-search-group" data-testid="search-group" data-channel-id={g.channel.id} aria-label={`Treffer in ${g.channel.name ?? 'Unterhaltung'}`}>
                    <header className="lfx-search-group__head">
                      <span className="lfx-search-group__name">{g.channel.name ? `#${g.channel.name}` : CHANNEL_TYPE_LABELS[g.channel.type]}</span>
                      <span className="lfx-muted">{CHANNEL_TYPE_LABELS[g.channel.type]}</span>
                      {srv && scope === 'all' && <span className="lfx-muted">· {srv.name}</span>}
                      {mirroredGroup && (
                        <Badge tone="mirror" testId="search-group-mirrored" title="Aus einem gespiegelten Discord-Server">
                          gespiegelt
                        </Badge>
                      )}
                    </header>
                    <ul className="lfx-search-hits">
                      {g.hits.map((h) => (
                        <li key={h.message.id}>
                          <div
                            role="link"
                            tabIndex={0}
                            className="lfx-search-hit"
                            data-testid="search-result"
                            data-message-id={h.message.id}
                            aria-label={`Zur Nachricht von ${displayName(h.message.author)} springen`}
                            onClick={() => void jump(h)}
                            onKeyDown={(e: KeyboardEvent) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                void jump(h);
                              }
                            }}
                          >
                            <MessagePreview message={h.message} highlight={h.highlight} mirrored={isMirroredServer(h.message.serverId ?? g.serverId)} jump={false} testId="search-result-message" />
                          </div>
                        </li>
                      ))}
                    </ul>
                  </section>
                );
              })
            )}
            {results.isFetchNextPageError && (
              <div className="lfx-error" role="alert" data-testid="search-more-error">
                {searchErrorMessage(results.error)}
              </div>
            )}
            {results.hasNextPage && (
              <Button testId="search-load-more" onClick={() => void results.fetchNextPage()} disabled={results.isFetchingNextPage}>
                {results.isFetchingNextPage ? 'Lädt …' : 'Mehr laden'}
              </Button>
            )}
          </section>
        )}
      </div>
    </ExtrasPage>
  );
}
